/**
 * @module SharedErrors
 * @package @geenius/i18n
 * @description Defines the typed error hierarchy for the shared Geenius i18n
 * runtime. These errors are re-used by framework bindings so consumers can
 * discriminate failure modes without relying on raw `Error` instances.
 */

import {
  AdapterConfigurationError,
  AdapterContextError,
  AdapterInvariantError,
  AdapterResolutionError,
} from "@geenius/adapters";
import type { ErrorCode } from "@geenius/errors";
import { GeeniusError } from "@geenius/errors";
import type { I18nNamespace } from "./types";

type I18nErrorCode = ErrorCode | (string & {});

export type I18nAdapterInfrastructureError =
  | AdapterConfigurationError
  | AdapterContextError
  | AdapterInvariantError
  | AdapterResolutionError;

export type I18nIntegrationError = I18nError | I18nAdapterInfrastructureError;

/**
 * Base error for all i18n runtime and integration failures.
 *
 * @param message Human-readable description of the failure.
 * @param code Stable machine-readable error code.
 * @param cause Underlying cause, if one exists.
 * @param context Structured metadata that helps callers diagnose the failure.
 */
export class I18nError extends GeeniusError {
  constructor(
    message: string,
    code: I18nErrorCode = "I18N_ERROR",
    cause?: unknown,
    context: Record<string, unknown> = {},
  ) {
    super({
      message,
      // `@geenius/errors` types `code` as the shared enum, but package-level
      // consumers also discriminate on Geenius-specific string codes.
      code: code as ErrorCode,
      cause,
      context,
    });
    // GeeniusError normalizes codes to its shared enum; i18n exposes a
    // package-specific code contract for translation and locale failures.
    Object.defineProperty(this as GeeniusError, "code", {
      value: code,
      enumerable: true,
      configurable: true,
    });
    this.name = "I18nError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error raised when an i18n configuration object is invalid.
 *
 * @param message Human-readable validation message.
 * @param context Structured config metadata that explains the invalid state.
 */
export class I18nConfigError extends I18nError {
  constructor(message: string, context: Record<string, unknown> = {}) {
    super(message, "INVALID_CONFIG", undefined, context);
    this.name = "I18nConfigError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error raised when a translation key cannot be found for a locale.
 *
 * @param key Missing translation key.
 * @param locale Locale where the lookup failed.
 */
export class TranslationNotFoundError extends I18nError {
  constructor(key: string, locale: string) {
    super(
      `Translation not found: "${key}" in locale "${locale}"`,
      "TRANSLATION_NOT_FOUND",
      undefined,
      { key, locale },
    );
    this.name = "TranslationNotFoundError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error raised when a caller requests an unsupported locale.
 *
 * @param locale Locale that was requested.
 * @param supported Supported locale codes available in the current runtime.
 */
export class LocaleNotSupportedError extends I18nError {
  constructor(locale: string, supported: readonly string[]) {
    super(
      `Locale "${locale}" not supported. Supported: ${supported.join(", ")}`,
      "LOCALE_NOT_SUPPORTED",
      undefined,
      {
        locale,
        supported,
      },
    );
    this.name = "LocaleNotSupportedError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error raised when a namespace fails to load for a locale.
 *
 * @param locale Locale whose namespace failed to load.
 * @param namespace Namespace identifier that failed.
 * @param cause Underlying network or parsing error, if available.
 */
export class TranslationLoadError extends I18nError {
  constructor(locale: string, namespace: I18nNamespace, cause?: unknown) {
    super(
      `Failed to load translations: ${locale}/${namespace}`,
      "LOAD_ERROR",
      cause,
      { locale, namespace },
    );
    this.name = "TranslationLoadError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Checks whether an unknown failure is an adapter infrastructure error that
 * should remain intact when i18n bridges an `@geenius/adapters` integration.
 *
 * @param error Unknown failure caught at an integration boundary.
 * @returns True when the value is an adapter configuration, resolution,
 * context, or invariant error.
 */
export function isAdapterInfrastructureError(
  error: unknown,
): error is I18nAdapterInfrastructureError {
  return (
    error instanceof AdapterConfigurationError ||
    error instanceof AdapterContextError ||
    error instanceof AdapterInvariantError ||
    error instanceof AdapterResolutionError
  );
}

/**
 * Normalizes i18n failures while preserving adapter infrastructure errors.
 *
 * Use this at boundaries that call into `@geenius/adapters` so errors such as
 * `AdapterContextError` and `AdapterInvariantError` keep their original class,
 * code, and context for upstream adapter-aware handling.
 *
 * @param error Unknown error value returned by an i18n or adapter boundary.
 * @param fallbackMessage Message used when the value cannot provide one.
 * @returns The original adapter infrastructure error, or a normalized
 * `I18nError` for non-adapter failures.
 */
export function toI18nIntegrationError(
  error: unknown,
  fallbackMessage = "An unexpected i18n integration error occurred.",
): I18nIntegrationError {
  if (isAdapterInfrastructureError(error)) {
    return error;
  }

  return toI18nError(error, fallbackMessage);
}

/**
 * Normalizes unknown failures into the shared `I18nError` hierarchy.
 *
 * @param error Unknown error value returned by an async boundary.
 * @param fallbackMessage Message used when the value cannot provide one.
 * @returns An `I18nError` instance safe to surface to consumers.
 */
export function toI18nError(
  error: unknown,
  fallbackMessage = "An unexpected i18n error occurred.",
): I18nError {
  if (error instanceof I18nError) {
    return error;
  }

  if (error instanceof GeeniusError) {
    return new I18nError(
      error.message,
      String(error.code) as I18nErrorCode,
      error.cause,
      error.context,
    );
  }

  if (error instanceof Error) {
    return new I18nError(fallbackMessage, "I18N_ERROR", error, {
      originalMessage: error.message,
    });
  }

  return new I18nError(fallbackMessage, "I18N_ERROR", error);
}
